import type { ReactNode } from 'react';

// The dashboard panel: a titled surface with an optional sub-line and a right-hand actions slot.
// Promoted out of AnalyticsPage when the platform cost board became the second page to need it.

interface CardProps {
  title: string;
  sub?: string;
  actions?: ReactNode;
  children: ReactNode;
}

export function Card({ title, sub, actions, children }: CardProps) {
  return (
    // min-w-0: when a Card sits in a CSS grid/flex row (e.g. AnalyticsPage's chart row), a grid/flex
    // item's automatic minimum width is its content's min-content size — which, for a child holding a
    // fixed-pixel-width SVG chart, is that chart's widest-ever rendered size. Without this, the first
    // time the chart grows, the grid column can never shrink back below that width again.
    <div className="min-w-0 rounded border border-line bg-surface p-5 shadow-1">
      <div className="mb-4 flex items-start justify-between gap-4">
        {/* min-w-0 on both cells: a flex child defaults to min-width:auto and so refuses to shrink
            below its content, which makes a wrappable actions slot (e.g. a 5-series chart legend)
            overflow the card instead of wrapping. */}
        <div className="min-w-0">
          <div className="text-sm font-semibold text-ink">{title}</div>
          {sub && <div className="text-[11px] text-muted">{sub}</div>}
        </div>
        {actions && <div className="min-w-0">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

// A colour key for a chart series. `dot` takes a CSS colour (usually a design token).
export function Legend({ dot, label }: { dot: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11.5px] text-muted">
      <span className="h-2.5 w-2.5 rounded-full" style={{ background: dot }} />
      {label}
    </span>
  );
}
